import axios from 'axios';
import logger from '../utils/logger';
import { getErrorMessage, logError, isRetryable } from '../utils/errorHandler';
import { SIMULATED_CHECKOUT_ENABLED } from '../config/productFlags';

// Get API URL from environment variable
// In development: Use relative path to leverage setupProxy.js
// In production: Set REACT_APP_API_URL to your production API URL
// If setupProxy.js is configured, use relative path '/api'
// Otherwise, use full URL 'http://localhost:5000/api'
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const ENABLE_SIMULATED_CHECKOUT = SIMULATED_CHECKOUT_ENABLED;

// Log the API URL being used (for debugging)
logger.log('API Base URL:', API_BASE_URL);

// Validate API URL is set
if (!process.env.REACT_APP_API_URL && process.env.NODE_ENV === 'production') {
  logger.warn('Warning: REACT_APP_API_URL is not set. Using default relative path.');
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 25000,
  withCredentials: true, // Enable sending cookies (HttpOnly) with requests
  // Note: 401 errors on /auth/me and /auth/refresh are EXPECTED when user is not authenticated
  // These will appear in browser console but are handled gracefully by the app
});

// CSRF double-submit for cookie-JWT mutations (Bearer clients unused in SPA).
let csrfToken = null;
let csrfPromise = null;

const ensureCsrfToken = async () => {
  if (csrfToken) return csrfToken;
  if (!csrfPromise) {
    csrfPromise = axios
      .get(`${API_BASE_URL}/auth/csrf`, { withCredentials: true })
      .then((res) => {
        csrfToken = res?.data?.csrfToken || null;
        return csrfToken;
      })
      .catch((err) => {
        logger.warn('CSRF token fetch failed:', err?.message || err);
        return null;
      })
      .finally(() => {
        csrfPromise = null;
      });
  }
  return csrfPromise;
};

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

// Routes reachable without auth. A 401 here must never bounce the visitor to
// /login — that includes public share links and marketing pages, not just
// the auth pages themselves. Keep in sync with the public <Route>s in App.js.
const PUBLIC_PATH_PREFIXES = [
  '/',
  '/login',
  '/signup',
  '/empresas',
  '/principios',
  '/pricing',
  '/reset-password',
  '/accept-invite',
  '/privacy',
  '/terms',
  '/share/',
];

const isPublicPagePath = (pathname) => {
  if (pathname === '/') return true;
  return PUBLIC_PATH_PREFIXES.some((prefix) => prefix !== '/' && pathname.startsWith(prefix));
};

const emitToast = (message, type = 'info') => {
  if (!message) return;
  try {
    window.dispatchEvent(new CustomEvent('oikra:toast', { detail: { message, type } }));
  } catch (_) {
    // ignore: toasts are best-effort
  }
};

// Request interceptor - HttpOnly cookies are automatically sent with requests
api.interceptors.request.use(
  async (config) => {
    // HttpOnly cookies are automatically sent with requests when withCredentials: true
    // Do NOT use localStorage tokens for security (prevents XSS attacks)
    const method = (config.method || 'get').toLowerCase();
    if (MUTATING_METHODS.has(method)) {
      const token = await ensureCsrfToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers['X-CSRFToken'] = token;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track if we're currently refreshing to avoid infinite loops
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

/** 401 on these routes means wrong credentials / unauthenticated action — never run refresh flow */
function isUnauthenticatedAuthRoute(url) {
  if (!url) return false;
  const markers = [
    '/auth/login',
    '/auth/register',
    '/auth/google',
    '/auth/logout',
    '/auth/password-reset/request',
    '/auth/password-reset/confirm',
    '/auth/invite/accept',
  ];
  return markers.some((m) => url.includes(m));
}

// Handle token expiration and network errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
      // Handle 401 Unauthorized (token expired or invalid)
      // Note: 401 errors on /auth/me and /auth/refresh are EXPECTED when user is not authenticated
      // These are normal and should not be treated as errors
      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        const requestUrl = originalRequest.url || '';
        // Wrong password, registration conflicts, etc. — pass through without refresh or noisy interceptor logs
        if (isUnauthenticatedAuthRoute(requestUrl)) {
          return Promise.reject(error);
        }

        const errorCode = error.response?.data?.error_code;
        const errorMessage = error.response?.data?.error;
        // Check both full URL and path for auth endpoints
        const requestPath = originalRequest.url?.replace(API_BASE_URL, '') || requestUrl;
        const isRefreshEndpoint =
          requestUrl.includes('/auth/refresh') || requestPath.includes('/auth/refresh');
        const isMeEndpoint =
          requestUrl.includes('/auth/me') || requestPath.includes('/auth/me');
        
        const isUnreadCount = requestUrl.includes('/notifications/unread-count');
        if (!isRefreshEndpoint && !isMeEndpoint && !isUnreadCount) {
          logger.error('401 Unauthorized error:', {
            url: originalRequest.url,
            errorCode,
            errorMessage
          });
        }
        
        // Never refresh the refresh call itself (would loop).
        if (isRefreshEndpoint) {
          error.isExpected = true;
          error.silent = true;
          const silentError = new Error('User not authenticated');
          silentError.response = error.response;
          silentError.config = error.config;
          silentError.isExpected = true;
          silentError.silent = true;
          return Promise.reject(silentError);
        }
        
        // For other endpoints, try to refresh token
        // Mark request as retried to prevent infinite loops
        originalRequest._retry = true;
        
        // Try to refresh token using HttpOnly cookies
        if (!isRefreshing) {
          isRefreshing = true;
          
          try {
            // Create a separate axios instance for refresh
            const refreshApi = axios.create({
              baseURL: API_BASE_URL,
              withCredentials: true, // Include HttpOnly cookies automatically
              headers: {
                'Content-Type': 'application/json',
              },
            });

            const token = await ensureCsrfToken();
            if (token) {
              refreshApi.defaults.headers['X-CSRFToken'] = token;
            }
            
            // Refresh token using HttpOnly cookies (automatically sent)
            // Do NOT use localStorage tokens for security
            await refreshApi.post('/auth/refresh');
            
            // HttpOnly cookies are automatically updated by the server
            // Do NOT store tokens in localStorage
            
            // Process queued requests
            processQueue(null, null);
            isRefreshing = false;
            
            // Retry original request (cookies are automatically sent)
            return api(originalRequest);
          } catch (refreshError) {
            // Refresh failed, logout
            processQueue(refreshError, null);
            isRefreshing = false;
            
            const currentPath = window.location.pathname;
            const isLoginPage = currentPath === '/login';
            const isSignupPage = currentPath === '/signup';
            const isPublicPage = isPublicPagePath(currentPath);

            try {
              window.dispatchEvent(new CustomEvent('oikra:session-expired'));
            } catch (_) {
              /* ignore */
            }
            localStorage.removeItem('user');
            if (!isPublicPage && !isLoginPage && !isSignupPage) {
              window.location.href = '/login';
            }
            refreshError.isExpected = true;
            refreshError.silent = true;
            return Promise.reject(refreshError);
          }
        } else {
          // Already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(() => {
              return api(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }
      }
    
    // Handle 403 Forbidden uniformly (permissions/subscription)
    if (error.response?.status === 403 && !error.silent) {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'No tienes permisos para realizar esta acción.';
      emitToast(msg, 'warning');
    }

    // Handle 429 Too Many Requests (rate limit)
    if (error.response?.status === 429 && !error.silent) {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Demasiadas solicitudes. Espera un momento e intenta nuevamente.';
      emitToast(msg, 'warning');
    }

    // Handle network errors (backend not running or connection issues)
    if (!error.response && error.message) {
      const isNetworkError = error.message.includes('Network Error') || 
                            error.code === 'ECONNREFUSED' ||
                            error.message.includes('ERR_CONNECTION_REFUSED') ||
                            error.message.includes('Failed to fetch');
      
      if (isNetworkError) {
        // More helpful error message with troubleshooting
        const errorMsg = process.env.NODE_ENV === 'development'
          ? 'Cannot connect to backend server. Please ensure:\n1. Backend is running on http://localhost:5000\n2. CORS is properly configured\n3. No firewall is blocking the connection'
          : 'Backend server is not available. Please contact support.';
        
        error.response = {
          data: { error: errorMsg },
          status: 503
        };
      }
    }

    // Optional/long-running endpoints: 503 or timeout is non-fatal, avoid console noise
    const requestUrl = originalRequest?.url || '';
    const isCalendarRequest = requestUrl.includes('/calendar/');
    const isPublicIndicators = requestUrl.includes('/indicators/public');
    const isPremiumComplete = requestUrl.includes('/reports/premium/complete');
    const isServiceUnavailable =
      error.response?.status === 503 ||
      error.code === 'ECONNABORTED' ||
      error.code === 'ERR_NETWORK';
    if ((isCalendarRequest || isPublicIndicators) && isServiceUnavailable) {
      error.silent = true;
    }
    const premiumNotReady =
      error.response?.data?.error_code === 'BIZ_7003' ||
      error.response?.data?.error_code === 'BIZ_7004' ||
      error.response?.data?.error_code === 'PREMIUM_REPORT_NOT_READY';
    if (
      isPremiumComplete &&
      error.response?.status === 404 &&
      (premiumNotReady || !error.response?.data?.error_code)
    ) {
      error.silent = true;
      error.isExpected = true;
    }
    
    // Use centralized error logging (only if not expected/silent)
    if (!error.isExpected && !error.silent) {
      logError(error, {
        url: originalRequest?.url,
        method: originalRequest?.method
      });
    }
    
    // Enhance error with user-friendly message (only if not expected)
    if (!error.isExpected && !error.silent && error.response?.data && !error.response.data.message) {
      error.userMessage = getErrorMessage(error);
      error.retryable = isRetryable(error);
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  googleLogin: (data) => api.post('/auth/google', data),
  logout: () => api.post('/auth/logout'),
  refreshToken: () => api.post('/auth/refresh'),
  getCurrentUser: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/me', data),
  changePassword: (data) => api.put('/auth/password', data),
  passwordResetRequest: (data) => api.post('/auth/password-reset/request', data),
  passwordResetConfirm: (data) => api.post('/auth/password-reset/confirm', data),
  acceptInvite: (data) => api.post('/auth/invite/accept', data),
  upgradeSubscription: (data) => api.post('/auth/upgrade', data),
  completeOnboarding: () => api.post('/auth/onboarding/complete'),
};

// Indicators API
export const indicatorsAPI = {
  getPublicIndicators: () => api.get('/indicators/public'),
  getIndicatorsList: () => api.get('/indicators/list'),
  getEconomicAreas: (countryCode = 'AR', options = {}) =>
    api.get('/indicators/economic-areas', {
      params: {
        country_code: countryCode,
        include_empty: options.includeEmpty !== false,
      },
    }),
  getIndicatorData: (code, params) => api.get(`/indicators/${code}`, { params }),
  getGuidedCapabilities: () => api.get('/indicators/guided-capabilities'),
};

// Atlas Económico API (/api/atlas/*, authenticated)
export const atlasAPI = {
  getConfig: () => api.get('/atlas/config'),
  // The one public call: the landing section of the published config, with today's featured chart resolved.
  getPublicLanding: () => api.get('/atlas/public/landing'),
  getCatalog: (params) => api.get('/atlas/catalog', { params }),
  getIndicator: (id, params) => api.get(`/atlas/indicators/${id}`, { params }),
  getSeries: (ids, params = {}) =>
    api.get('/atlas/series', { params: { ...params, ids: [].concat(ids).join(',') } }),
  getPulse: (params) => api.get('/atlas/pulse', { params }),
  propose: (ids, params = {}) =>
    api.get('/atlas/propose', { params: { ...params, ids: [].concat(ids).join(',') } }),
  getCoverage: (country = 'AR') => api.get('/atlas/coverage', { params: { country } }),
  getTopic: (slug, country = 'AR') => api.get(`/atlas/topics/${slug}`, { params: { country } }),
  listSavedWidgets: () => api.get('/atlas/saved-widgets'),
  saveWidget: (widget, extra = {}) => api.post('/atlas/saved-widgets', { widget, ...extra }),
  getSavedWidget: (id) => api.get(`/atlas/saved-widgets/${id}`),
  updateSavedWidget: (id, patch) => api.patch(`/atlas/saved-widgets/${id}`, patch),
  deleteSavedWidget: (id) => api.delete(`/atlas/saved-widgets/${id}`),
  listPublicViews: (params = {}) => api.get('/atlas/views/public', { params }),
  // Indicators the person follows (the Home's Seguimiento).
  listFollows: () => api.get('/atlas/follows'),
  follow: (indicatorId) => api.post('/atlas/follows', { indicator_id: indicatorId }),
  unfollow: (indicatorId) => api.delete(`/atlas/follows/${encodeURIComponent(indicatorId)}`),
};

// Reports API
export const reportsAPI = {
  generateReport: (data) => api.post('/reports/generate', data),
  getUserReports: (params) => api.get('/reports/list', { params }),
  getReport: (id) => api.get(`/reports/${id}`),
  getDailyPremiumReport: () => api.get('/reports/premium/daily'),
  getPremiumReportPreviewOutline: (template, countryCode = 'AR') =>
    api.get('/reports/premium/preview-outline', {
      params: { ...(template ? { template } : {}), country_code: countryCode },
    }),
  listPremiumReportTemplates: (params) =>
    api.get('/reports/premium/templates', { params }),
  createPremiumReportTemplate: (data) =>
    api.post('/reports/premium/templates', data),
  listPremiumPublications: (params) =>
    api.get('/reports/premium/publications', { params, timeout: 30000 }),
  createPremiumPublication: (data) =>
    api.post('/reports/premium/publications', data, { timeout: 120000 }),
  getPremiumPublication: (editionKey) =>
    api.get(`/reports/premium/publications/${encodeURIComponent(editionKey)}`),
  updatePremiumPublication: (editionKey, data) =>
    api.patch(`/reports/premium/publications/${encodeURIComponent(editionKey)}`, data),
  generatePremiumPublication: (editionKey) =>
    api.post(
      `/reports/premium/publications/${encodeURIComponent(editionKey)}/generate`,
      {},
      { timeout: 120000 },
    ),
  publishPremiumPublication: (editionKey, data = {}) =>
    api.post(`/reports/premium/publications/${encodeURIComponent(editionKey)}/publish`, data),
  includePremiumPublication: (parentEditionKey, data) =>
    api.post(
      `/reports/premium/publications/${encodeURIComponent(parentEditionKey)}/includes`,
      data,
    ),
  removePremiumPublicationInclusion: (parentEditionKey, childEditionKey) =>
    api.delete(`/reports/premium/publications/${encodeURIComponent(parentEditionKey)}/includes`, {
      data: { child_edition_key: childEditionKey },
    }),
  requestPremiumReportGenerate: (data) =>
    api.post('/reports/premium/generate', data, { timeout: 120000 }),
  getPremiumReportJobStatus: (jobId) =>
    api.get(`/reports/premium/status/${jobId}`, { timeout: 30000 }),
  getCompletePremiumReport: (month, year, options = {}) =>
    api.get('/reports/premium/complete', {
      params: {
        month,
        year,
        country_code: options.countryCode || options.countryIso || options.country_iso || 'AR',
        ...(options.fallbackLatest ? { fallback_latest: '1' } : {}),
        ...(options.forceRefresh ? { force_refresh: '1' } : {}),
      },
      timeout: 120000,
    }),
  listPremiumEditions: (params) => api.get('/reports/premium/editions', { params, timeout: 30000 }),
  exportPremiumComplete: (data, config = {}) =>
    api.post('/reports/premium/export', data, {
      responseType: 'blob',
      timeout: 120000,
      ...config,
    }),
  visualizeData: (data) => api.post('/reports/visualize', data, {
    timeout: 120000, // 2 minutes timeout for visualization
  }),
  compareDatasets: (data) => api.post('/reports/visualize/compare', data, {
    timeout: 120000, // 2 minutes timeout for comparison
  }),
  // Visualizations endpoints
  getVisualizations: (params) => api.get('/reports/visualizations', { params }),
  getVisualization: (id) => api.get(`/reports/visualizations/${id}`),
  getVisualizationsBySection: (section, params) => api.get(`/reports/visualizations/by-section/${section}`, { params }),
  getMyVisualizationHistory: (params) => api.get('/reports/visualizations/my-history', { params, timeout: 20000 }),
  updateMyVisualization: (id, data) => api.patch(`/reports/visualizations/${id}`, data),
  shareVisualization: (id) => api.post(`/reports/visualizations/${id}/share`),
  getSharedVisualization: (token) => api.get(`/reports/share/${token}`),
  exportDashboardToPDF: (data) => api.post('/reports/export/pdf', data, {
    responseType: 'blob', // Important for PDF download
    timeout: 120000, // 2 minutes timeout for PDF generation
  }),
};

// Semantic concepts / topics API
// List is registered at /api/concepts (no trailing slash). With APPEND_SLASH=False,
// /api/concepts/ falls into the include() and 404s as a missing slug.
export const conceptsAPI = {
  listConcepts: () => api.get('/concepts', { timeout: 30000 }),
  getConcept: (slug, params) =>
    api.get(`/concepts/${encodeURIComponent(slug)}`, { params, timeout: 30000 }),
  // Must exceed backend visualizer worst case: (VISUALIZER_MAX_RETRIES+1) * SQL_AGENT_QUERY_TIMEOUT + backoff
  executeTemplate: (templateId, body = {}) =>
    api.post(`/concepts/templates/${encodeURIComponent(templateId)}/execute`, body, {
      timeout: 360000,
    }),
  getAuditSummary: (params) => api.get('/concepts/audit/summary', { params }),
};

// Chat API
export const chatAPI = {
  processQuery: (data) => api.post('/chat/query', data),
  getSession: () => api.get('/chat/session'),
  saveSession: (data) => api.put('/chat/session', data),
  listConversations: () => api.get('/chat/sessions'),
  createConversation: (title) => api.post('/chat/sessions', { title }),
  getConversation: (id) => api.get(`/chat/sessions/${id}`),
  deleteConversation: (id) => api.delete(`/chat/sessions/${id}`),
  getHistory: (params) => api.get('/chat/history', { params, timeout: 20000 }),
  getVizPreferences: () => api.get('/chat/preferences'),
  updateVizPreferences: (data) => api.put('/chat/preferences', data),
  saveVisualization: (data) => api.post('/chat/visualizations/save', data),
  getSavedVisualizations: (params) => api.get('/chat/visualizations/saved', { params }),
  compareIndicators: (data) => api.post('/chat/compare', data),
  multiIndicator: (data) => api.post('/chat/multi', data),
  exportToWorkspace: (data) => api.post('/chat/export-to-workspace', data),
  exportToPackage: (data) => api.post('/chat/export-to-package', data),
};

// News API
export const newsAPI = {
  getNewsList: (params) => api.get('/news/list', { params, timeout: 20000 }),
  getArticle: (id) => api.get(`/news/${id}`),
  getNewsletters: (params) => api.get('/news/newsletters', { params }),
  getNewsletterVisualizations: (articleId) => api.get(`/news/newsletters/${articleId}/visualizations`),
  getNewsletterCalendar: (params) => api.get('/news/newsletters/calendar', { params }),
  addFavorite: (id) => api.post(`/news/newsletters/${id}/favorite`),
  removeFavorite: (id) => api.delete(`/news/newsletters/${id}/favorite`),
  getFavorites: (params) => api.get('/news/newsletters/favorites', { params }),
  shareNewsletter: (id) => api.post(`/news/newsletters/${id}/share`),
  getReadingStats: (id) => api.get(`/news/newsletters/${id}/reading-stats`),
  startReading: (id) => api.post(`/news/newsletters/${id}/reading-stats`),
  updateReadingStats: (id, data) => api.put(`/news/newsletters/${id}/reading-stats`, data),
  addToChat: (id) => api.post(`/news/newsletters/${id}/add-to-chat`),
};

// Data Packages API (Marketplace)
export const dataPackagesAPI = {
  checkAccess: () => api.get('/data/packages/access'),
  listPackages: (params) => api.get('/data/packages', { params }),
  getPackage: (id) => api.get(`/data/packages/${id}`),
  getPackagePreview: (id) => api.get(`/data/packages/${id}/preview`),
  purchasePackage: (id) => api.post(`/data/packages/${id}/purchase`),
  requestPreview: (id) => api.post(`/data/packages/${id}/request-preview`),
  getMyPurchases: (params) => api.get('/data/packages/my-purchases', { params }),
};

// Premium Report API
export const premiumReportAPI = {
  getPreferences: () => api.get('/premium-report/preferences'),
  updatePreferences: (data) => api.put('/premium-report/preferences', data),
  getNotes: (params) => api.get('/premium-report/notes', { params }),
  // Batch load notes for multiple sections with rate limiting
  getNotesBatch: async (sectionKeys) => {
    // If backend supports batch endpoint, use it
    // Otherwise, make parallel requests with rate limiting
    if (sectionKeys.length === 0) return Promise.resolve({ data: { notes: {} } });
    
    // Make requests in smaller batches to avoid rate limiting
    const batchSize = 2; // Reduced from 3 to 2
    const batches = [];
    for (let i = 0; i < sectionKeys.length; i += batchSize) {
      batches.push(sectionKeys.slice(i, i + batchSize));
    }
    
    const allNotes = {};
    // Mutated via property access (not reassigned) so the per-request closures below
    // don't capture a rebound loop variable.
    const rateLimitState = { consecutive429s: 0 };
    const maxConsecutive429s = 3; // Stop after 3 consecutive 429s

    // Process batches sequentially with delay
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];

      // Increased delay between batches, especially after 429s
      if (batchIndex > 0) {
        const delay = rateLimitState.consecutive429s > 0 ? 2000 : 1000; // 2s after 429, 1s normally
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      // Process batch in parallel
      const batchResults = await Promise.all(
        batch.map(sectionKey =>
          api.get('/premium-report/notes', { params: { section_key: sectionKey } })
            .then(res => {
              rateLimitState.consecutive429s = 0; // Reset counter on success
              return { sectionKey, notes: res.data.notes || [] };
            })
            .catch(err => {
              if (err.response?.status === 429) {
                rateLimitState.consecutive429s++;
                // If too many 429s, stop processing
                if (rateLimitState.consecutive429s >= maxConsecutive429s) {
                  logger.warn(`Too many rate limit errors (429), stopping batch loading. Remaining sections will load on demand.`);
                }
              } else {
                // Only log non-429 errors
                logger.error(`Error loading notes for ${sectionKey}:`, err);
              }
              return { sectionKey, notes: [], error: err };
            })
        )
      );

      // Merge results
      batchResults.forEach(({ sectionKey, notes }) => {
        allNotes[sectionKey] = notes;
      });

      // Stop if we hit too many 429s
      if (rateLimitState.consecutive429s >= maxConsecutive429s) {
        logger.warn(`Stopping batch loading due to rate limiting. Loaded ${Object.keys(allNotes).length} of ${sectionKeys.length} sections.`);
        break;
      }
    }
    
    return { data: { notes: allNotes } };
  },
  createNote: (data) => api.post('/premium-report/notes', data),
  updateNote: (id, data) => api.put(`/premium-report/notes/${id}`, data),
  deleteNote: (id) => api.delete(`/premium-report/notes/${id}`),
  getVersions: (params) => api.get('/premium-report/versions', { params }),
  compareVersions: (params) => api.get('/premium-report/versions/compare', { params }),
  exportSection: (data) => api.post('/premium-report/export', data),
  importFromPackage: (data) => api.post('/premium-report/import', data),
};

// Subscription API
export const subscriptionAPI = {
  getPlans: () => api.get('/subscription/plans'),
  getEntitlements: () => api.get('/subscription/entitlements'),
  checkoutSimulated: (data) => {
    if (!ENABLE_SIMULATED_CHECKOUT) {
      return Promise.reject({
        response: {
          status: 410,
          data: {
            message: 'El checkout simulado está deshabilitado. Contactar ventas.',
            error_code: 'SIMULATED_CHECKOUT_DISABLED',
            retryable: false,
          },
        },
      });
    }
    return api.post('/subscription/checkout-simulated', data);
  },
  upgradeSimulated: (data) => {
    if (!ENABLE_SIMULATED_CHECKOUT) {
      return Promise.reject({
        response: {
          status: 410,
          data: {
            message: 'El upgrade simulado está deshabilitado. Contactar ventas.',
            error_code: 'SIMULATED_CHECKOUT_DISABLED',
            retryable: false,
          },
        },
      });
    }
    return api.put('/subscription/upgrade-simulated', data);
  },
  sendConfirmationEmail: (data) => {
    if (!ENABLE_SIMULATED_CHECKOUT) {
      return Promise.reject({
        response: {
          status: 410,
          data: {
            message: 'El email de checkout simulado está deshabilitado. Contactar ventas.',
            error_code: 'SIMULATED_CHECKOUT_DISABLED',
            retryable: false,
          },
        },
      });
    }
    return api.post('/subscription/send-confirmation-email', data);
  },
};

export const billingAPI = {
  getInvoices: (params) => api.get('/billing/invoices', { params }),
};

// Dashboard API
export const dashboardAPI = {
  getPopularVisualizations: (params) => api.get('/dashboard/popular-visualizations', { params }),
  getDashboardConfig: (params) => api.get('/dashboard/config', { params }),
  createDashboardConfig: (data) => api.post('/dashboard/config', data),
  updateDashboardConfig: (id, data) => api.put(`/dashboard/config/${id}`, data),
  deleteDashboardConfig: (id) => api.delete(`/dashboard/config/${id}`),
  listDashboardConfigs: () => api.get('/dashboard/configs'),
  // Put something on the Home board from any screen: { type, data, dashboard_name? }.
  pin: (payload) => api.post('/dashboard/pins', payload),
  // Put the initial board back (what an admin published, or the built-in one); it replaces the board.
  restoreDefault: (dashboardName = 'default') => api.post('/dashboard/restore-default', { dashboard_name: dashboardName }),
  // The person is on the Home now: when they were last here and which indicators they watch have a new period.
  visit: () => api.post('/dashboard/visit'),
  unpin: (widgetId, dashboardName = 'default') =>
    api.delete(`/dashboard/pins/${encodeURIComponent(widgetId)}`, { params: { dashboard_name: dashboardName } }),
};

// Visualizations API (for enhanced visualization interface)
export const visualizationsAPI = {
  calculateMeasures: (data) => api.post('/visualizations/calculate-measures', data),
  addCalculatedColumn: (data) => api.post('/visualizations/add-calculated-column', data),
  filterAndGroup: (data) => api.post('/visualizations/filter-group', data),
  saveAsNew: (data) => api.post('/visualizations/save-as-new', data),
};

// Notifications API
export const notificationsAPI = {
  getNotifications: (params) => api.get('/notifications', { params }),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  deleteNotification: (id) => api.delete(`/notifications/${id}`),
  getUnreadCount: () => api.get('/notifications/unread-count', { timeout: 8000 }),
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (data) => api.put('/notifications/preferences', data),
};

// Alerts API
export const alertsAPI = {
  getAlerts: (params) => api.get('/alerts', { params }),
  getAlert: (id) => api.get(`/alerts/${id}`),
  createAlert: (data) => api.post('/alerts', data),
  updateAlert: (id, data) => api.put(`/alerts/${id}`, data),
  deleteAlert: (id) => api.delete(`/alerts/${id}`),
  testAlert: (id) => api.post(`/alerts/${id}/test`),
  previewAlert: (data) => api.post('/alerts/preview', data),
  updateMessagingChannels: (data) => api.put('/alerts/channels', data),
};

// Admin API
export const adminAPI = {
  getUsers: (params) => api.get('/admin/users', { params }),
  createUser: (data) => api.post('/admin/users', data),
  createInvite: (data) => api.post('/admin/invites', data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  updateUserSubscription: (userId, data) => api.put(`/admin/users/${userId}/subscription`, data),
  getAnalytics: () => api.get('/admin/analytics'),
  getAuditLog: (params) => api.get('/admin/audit-log', { params }),
  createArticle: (data) => api.post('/admin/articles', data),
  updateArticle: (id, data) => api.put(`/admin/articles/${id}`, data),
  deleteArticle: (id) => api.delete(`/admin/articles/${id}`),
  getNewsletters: (params) => api.get('/admin/newsletters', { params }),
  // Visualizations endpoints (admin only)
  getVisualizations: (params) => api.get('/admin/visualizations', { params }),
  getVisualization: (id) => api.get(`/admin/visualizations/${id}`),
  createVisualization: (data) => api.post('/admin/visualizations', data, {
    timeout: 120000, // 2 minutes timeout for visualization
  }),
  updateVisualization: (id, data) => api.put(`/admin/visualizations/${id}`, data),
  deleteVisualization: (id) => api.delete(`/admin/visualizations/${id}`),
  executeVisualization: (id) => api.post(`/admin/visualizations/${id}/execute`, {}, {
    timeout: 120000, // 2 minutes timeout for visualization
  }),
  // Data Packages endpoints (admin only)
  getDataPackages: (params) => api.get('/data/packages', { params }),
  getDataPackage: (id) => api.get(`/data/packages/${id}`),
  createDataPackage: (data) => api.post('/data/packages', data),
  updateDataPackage: (id, data) => api.put(`/data/packages/${id}`, data),
  deleteDataPackage: (id) => api.delete(`/data/packages/${id}`),
  // Premium Report Sections endpoints (admin only)
  getPremiumReportSections: (params) => api.get('/admin/premium-report/sections', { params }),
  getPremiumReportSectionTree: (params) => api.get('/admin/premium-report/sections/tree', { params }),
  getPremiumReportSection: (id) => api.get(`/admin/premium-report/sections/${id}`),
  createPremiumReportSection: (data) => api.post('/admin/premium-report/sections', data),
  updatePremiumReportSection: (id, data) => api.put(`/admin/premium-report/sections/${id}`, data),
  deletePremiumReportSection: (id) => api.delete(`/admin/premium-report/sections/${id}`),
  reorderPremiumReportSections: (orderData) => api.post('/admin/premium-report/sections/reorder', orderData),
  getAvailableGeneratorMethods: () => api.get('/admin/premium-report/sections/available-methods'),
  seedPremiumReportSections: (params) =>
    api.post('/admin/premium-report/sections/seed', {}, { params }),
  getPremiumReportEditionStatus: (month, year, countryIso = 'AR') =>
    api.get('/admin/premium-report/editions/status', {
      params: { month, year, country_iso: countryIso },
    }),
  generatePremiumReportEdition: (data) =>
    api.post('/admin/premium-report/editions/generate', data, { timeout: 120000 }),
  cancelPremiumReportEditionJob: (data) =>
    api.post('/admin/premium-report/editions/cancel-job', data),
  publishPremiumReportEdition: (data) =>
    api.post('/admin/premium-report/editions/publish', data),
  getPremiumReportEditionOutline: (month, year, countryIso = 'AR') =>
    api.get('/admin/premium-report/editions/outline', {
      params: { month, year, country_iso: countryIso },
    }),
  getPremiumReportEditionLayout: (month, year, countryIso = 'AR') =>
    api.get('/admin/premium-report/editions/layout', {
      params: { month, year, country_iso: countryIso },
    }),
  savePremiumReportEditionLayout: (data) => api.put('/admin/premium-report/editions/layout', data),
  copyPremiumReportEditionFrom: (data) => api.post('/admin/premium-report/editions/copy-from', data),
  getPremiumReportEditionBlock: ({ month, year, section_key, subsection_key = '', country_iso = 'AR' }) =>
    api.get('/admin/premium-report/editions/block', {
      params: { month, year, section_key, subsection_key, country_iso },
    }),
  savePremiumReportEditionBlock: (data) => api.put('/admin/premium-report/editions/block', data),
  deletePremiumReportEditionBlock: (data) =>
    api.delete('/admin/premium-report/editions/block', { data }),
  regeneratePremiumReportSection: (data) =>
    api.post('/admin/premium-report/editions/regenerate-section', data, { timeout: 300000 }),
  getPremiumReportFreeBlocks: (month, year, countryIso = 'AR') =>
    api.get('/admin/premium-report/editions/free-blocks', {
      params: { month, year, country_iso: countryIso },
    }),
  createPremiumReportFreeBlock: (data) => api.post('/admin/premium-report/editions/free-blocks', data),
  deletePremiumReportFreeBlock: (blockId) =>
    api.delete(`/admin/premium-report/editions/free-blocks/${blockId}`),
  getPremiumReportBlockTemplates: () => api.get('/admin/premium-report/block-templates'),
  createPremiumReportBlockTemplate: (data) => api.post('/admin/premium-report/block-templates', data),
  applyPremiumReportBlockTemplate: (data) =>
    api.post('/admin/premium-report/block-templates/apply', data),
  getPremiumReportJobStatus: (jobId) =>
    api.get(`/reports/premium/status/${jobId}`, { timeout: 30000 }),
  getAdminIndicatorCodes: () => api.get('/admin/indicators/codes'),
  getAdminIndicatorDataReadiness: () =>
    api.get('/admin/indicators/data-readiness', { timeout: 30000 }),
  getAdminCalendarEvents: (params) => api.get('/admin/calendar-events', { params }),
  createAdminCalendarEvent: (data) => api.post('/admin/calendar-events', data),
  updateAdminCalendarEvent: (id, data) => api.put(`/admin/calendar-events/${id}`, data),
  deleteAdminCalendarEvent: (id) => api.delete(`/admin/calendar-events/${id}`),
  getDataNotes: (params) => api.get('/admin/data-notes', { params }),
  createDataNote: (data) => api.post('/admin/data-notes', data),
  updateDataNote: (id, data) => api.put(`/admin/data-notes/${id}`, data),
  deleteDataNote: (id) => api.delete(`/admin/data-notes/${id}`),
  previewDataNoteTrino: (data) => api.post('/admin/data-notes/preview-trino', data),
  getSemanticConcepts: () => api.get('/admin/semantic/concepts'),
  affiliateConceptSlug: (data) => api.post('/admin/semantic/affiliate', data),
  // Atlas configuration (draft, publish, history)
  getAtlasDraft: () => api.get('/admin/atlas/draft'),
  patchAtlasDraft: (section, value, expectedRevision) =>
    api.patch('/admin/atlas/draft', { section, value, expected_revision: expectedRevision }),
  validateAtlasDraft: () => api.post('/admin/atlas/draft/validate'),
  discardAtlasDraft: () => api.post('/admin/atlas/draft/discard'),
  publishAtlas: (note, expectedRevision) =>
    api.post('/admin/atlas/publish', { note, expected_revision: expectedRevision }),
  getAtlasVersions: () => api.get('/admin/atlas/versions'),
  getAtlasVersion: (versionNumber) => api.get(`/admin/atlas/versions/${versionNumber}`),
  rollbackAtlas: (versionNumber, note) =>
    api.post(`/admin/atlas/versions/${versionNumber}/rollback`, { note }),
  getAtlasCatalogHealth: () => api.get('/admin/atlas/catalog-health'),
  // Saved charts: every chart people built, private or public (nothing is private to an administrator)
  listAtlasViews: (params = {}) => api.get('/admin/atlas/views', { params }),
  setAtlasViewVisibility: (id, visibility) => api.patch(`/admin/atlas/views/${id}`, { visibility }),
  deleteAtlasView: (id) => api.delete(`/admin/atlas/views/${id}`),
  previewAtlasWidgets: (topic) => api.post('/admin/atlas/preview-widgets', { topic }),
  // The landing's featured chart as a visitor would get it, for the landing as edited (nothing is saved).
  previewAtlasLanding: (landing, offset = 0) => api.post('/admin/atlas/landing-preview', { landing, offset }),
};

export const dataNotesAPI = {
  list: (params) => api.get('/data-notes', { params }),
  getBySlug: (slug) => api.get(`/data-notes/${encodeURIComponent(slug)}`),
};

// Content Generation API
export const contentAPI = {
  generateWeeklyNewsletter: (data) => api.post('/content/generate/weekly-newsletter', data, {
    timeout: 60000, // 1 minute timeout for generation
  }),
  generateInsightArticle: (data) => api.post('/content/generate/insight-article', data, {
    timeout: 60000,
  }),
  generateAlert: (data) => api.post('/content/generate/alert', data, {
    timeout: 60000,
  }),
  getGenerationJobs: (params) => api.get('/content/generation-jobs', { params }),
  getGenerationJob: (id) => api.get(`/content/generation-jobs/${id}`),
  getGeneratedArticles: (params) => api.get('/content/articles/generated', { params }),
  updateArticle: (id, data) => api.put(`/content/articles/${id}`, data),
  publishArticle: (id, data) => api.post(`/content/articles/${id}/publish`, data),
  deleteArticle: (id) => api.delete(`/content/articles/${id}`),
  getTemplates: (params) => api.get('/content/templates', { params }),
  getTemplate: (id) => api.get(`/content/templates/${id}`),
  createTemplate: (data) => api.post('/content/templates', data),
  updateTemplate: (id, data) => api.put(`/content/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/content/templates/${id}`),
  // Schedules API
  getSchedules: (params) => api.get('/content/schedules', { params }),
  getSchedule: (id) => api.get(`/content/schedules/${id}`),
  createSchedule: (data) => api.post('/content/schedules', data),
  updateSchedule: (id, data) => api.put(`/content/schedules/${id}`, data),
  deleteSchedule: (id) => api.delete(`/content/schedules/${id}`),
  runSchedule: (id) => api.post(`/content/schedules/${id}/run`),
  // Analytics API
  trackContentView: (data) => api.post('/content/analytics/track', data),
  getArticleAnalytics: (id) => api.get(`/content/analytics/article/${id}`),
  getAnalyticsDashboard: (params) => api.get('/content/analytics/dashboard', { params }),
};

// Economic Calendar API
export const calendarAPI = {
  getEconomicCalendar: (params) => api.get('/calendar/economic', { params, timeout: 15000 }), // 15 seconds for calendar queries
  getUpcomingEvents: (params) => api.get('/calendar/economic/upcoming', { params, timeout: 15000 }), // 15 seconds for upcoming events
};



export default api;
